'use client';
import Icon from '@/components/ui/AppIcon';

const services = [
  {
    id: 'fullstack',
    icon: 'CodeBracketSquareIcon',
    title: 'Full Stack Development (Websites & Web Apps)',
    description:
      'End-to-end websites and web applications built with modern frameworks — from React, Next.js, Vue.js, and Angular frontends to Node.js, Python, and PHP/Laravel backends, fully scalable, performant, and maintainable. Database design, cloud deployment, DevOps, and full-stack optimization, delivering production-ready solutions that are secure, fast, and reliable.',
    tags: [
      'React',
      'Next.js',
      'Vue.js',
      'Angular',
      'Node.js',
      'Python',
      'PHP',
      'Laravel',
      'TypeScript',
      'AWS',
    ],
    color: '#6C63FF',
    size: 'large',
    gradient: 'linear-gradient(135deg, rgba(108,99,255,0.15), rgba(0,212,255,0.05))',
  },
  {
    id: 'mobile',
    icon: 'DevicePhoneMobileIcon',
    title: 'Mobile App Design & Development',
    description:
      'Native Android and cross-platform apps using Flutter, React Native, Java and Kotlin. Fully optimized and ready for App Store and Play Store deployment.',
    tags: ['Flutter', 'React Native', 'Java', 'Kotlin', 'Dart', 'Firebase', 'TypeScript'],
    color: '#00D4FF',
    gradient: 'linear-gradient(135deg, rgba(0,212,255,0.12), rgba(108,99,255,0.05))',
  },
  {
    id: 'design',
    icon: 'SwatchIcon',
    title: 'UI/UX & Brand Design',
    description:
      'Pixel-perfect interfaces, design systems, brand identities, and motion graphics that captivate and convert.',
    tags: ['Figma', 'Adobe XD', 'Photoshop', 'Illustrator'],
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(255,107,107,0.05))',
  },
  {
    id: 'devops',
    icon: 'ServerStackIcon',
    title: 'DevOps & Cloud',
    description:
      'CI/CD pipelines, containerization, Kubernetes orchestration, and enterprise-grade cloud architecture on AWS, GCP, and Azure.',
    tags: ['Docker', 'K8s', 'AWS', 'GCP', 'Terraform', 'Oracle Cloud'],
    color: '#10D9A0',
    size: 'tall',
    gradient: 'linear-gradient(135deg, rgba(16,217,160,0.12), rgba(0,212,255,0.05))',
  },
  {
    id: 'ecommerce',
    icon: 'ShoppingCartIcon',
    title: 'E-Commerce Solutions',
    description:
      'High-converting online stores on Shopify, WooCommerce, and custom platforms. Payment gateways, inventory management, and analytics.',
    tags: ['Shopify', 'WooCommerce', 'Magento', 'Medusa', 'Stripe', 'PayPal'],
    color: '#FF6B6B',
    gradient: 'linear-gradient(135deg, rgba(255,107,107,0.12), rgba(245,158,11,0.05))',
  },
  {
    id: 'animation',
    icon: 'FilmIcon',
    title: 'Graphics, Video & Animation',
    description:
      'Motion graphics, explainer videos, brand animations, and social media content using After Effects, Premiere Pro, and Cinema 4D.',
    tags: ['Photoshop', 'Illustrator', 'After Effects', 'Premiere Pro', 'Cinema 4D'],
    color: '#A855F7',
    gradient: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(108,99,255,0.05))',
  },
  {
    id: 'cms',
    icon: 'DocumentTextIcon',
    title: 'CMS & Headless',
    description:
      'WordPress, Contentful, Sanity, and custom CMS solutions. Headless architecture for maximum performance and flexibility.',
    tags: ['Shopify', 'WordPress', 'Contentful', 'Sanity', 'Strapi', 'DirectUs'],
    color: '#0EA5E9',
    gradient: 'linear-gradient(135deg, rgba(14,165,233,0.12), rgba(16,217,160,0.05))',
  },
  {
    id: 'lead',
    icon: 'UserGroupIcon',
    title: 'Tech Leadership',
    description:
      'Technical team leadership, architecture decisions, code reviews, agile sprint management, and mentoring for high-performance engineering teams.',
    tags: ['Agile/Scrum', 'Jira', 'Git Flow', 'GitHub', 'BitBucket', 'Code Review'],
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(108,99,255,0.05))',
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="py-16 lg:py-20 relative">
      {/* Background decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(108,99,255,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 initially-hidden animate-in">
          <div className="section-label mb-3">What I Do</div>
          <h2
            className="text-4xl md:text-6xl font-extrabold mb-4"
            style={{ color: 'var(--text-primary)', letterSpacing: '-0.04em' }}
          >
            Services &amp; <span className="gradient-text">Expertise</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            From concept to deployment — I cover every layer of the digital stack. One partner.
            Complete solutions.
          </p>
        </div>

        {/* Bento Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          style={{ gridAutoRows: 'auto' }}
        >
          {services.map((service, i) => (
            <div
              key={service.id}
              className={`service-card glass-card initially-hidden animate-in ${
                service.size === 'large' ? 'lg:col-span-2' : ''
              }`}
              style={{
                animationDelay: `${i * 0.08}s`,
                background: service.gradient,
                border: `1px solid ${service.color}20`,
              }}
            >
              {/* Icon */}
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{
                  background: `${service.color}20`,
                  border: `1px solid ${service.color}30`,
                }}
              >
                <Icon
                  name={service.icon as 'CodeBracketSquareIcon'}
                  size={28}
                  style={{ color: service.color }}
                />
              </div>

              {/* Content */}
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
              >
                {service.title}
              </h3>
              <p
                className="text-sm leading-relaxed mb-5"
                style={{ color: 'var(--text-secondary)' }}
              >
                {service.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-mono font-medium px-3 py-1 rounded-full"
                    style={{
                      background: `${service.color}15`,
                      color: service.color,
                      border: `1px solid ${service.color}25`,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Hover arrow */}
              <div
                className="mt-5 flex items-center gap-2 text-sm font-semibold"
                style={{ color: service.color }}
              >
                <span>Learn more</span>
                <Icon name="ArrowRightIcon" size={16} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
